/**
 * ICloudSyncSettingsPage - Admin settings for Apple iCloud Contacts sync
 * Allows configuring Apple ID and app-specific password for contact synchronization
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { 
  ArrowLeft, Cloud, Save, Loader2, Shield, ExternalLink, Info, 
  CheckCircle2, RefreshCw, Users, AlertTriangle, Eye, EyeOff,
  Smartphone
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Apple Icon SVG
const AppleIcon = ({ size = 24, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const ICloudSyncSettingsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState({
    configured: false,
    enabled: false,
    apple_id: null,
    last_sync: null,
    total_synced: 0,
    last_error: null
  });
  const [settings, setSettings] = useState({
    apple_id: '',
    app_password: '',
    enabled: true,
    sync_on_create: true,
    sync_on_update: true,
    contact_group: 'StrongFit Kunden',
    inactive_group: 'StrongFit Inaktiv'
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Load status and settings in parallel
      const [statusRes, settingsRes] = await Promise.all([
        axios.get(`${API}/icloud/status`, { headers }),
        axios.get(`${API}/icloud/settings`, { headers })
      ]);
      
      setStatus(statusRes.data);
      if (settingsRes.data.configured) {
        setSettings({
          apple_id: settingsRes.data.apple_id || '',
          app_password: '', // Never returned from API
          enabled: settingsRes.data.enabled,
          sync_on_create: settingsRes.data.sync_on_create,
          sync_on_update: settingsRes.data.sync_on_update,
          contact_group: settingsRes.data.contact_group || 'StrongFit Kunden',
          inactive_group: settingsRes.data.inactive_group || 'StrongFit Inaktiv'
        });
      }
    } catch (error) {
      console.error('Load settings error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validate
    if (!settings.apple_id) {
      toast.error('Bitte Apple ID eingeben');
      return;
    }
    if (!status.configured && !settings.app_password) {
      toast.error('Bitte App-Passwort eingeben');
      return;
    }
    
    // Validate app-specific password format
    if (settings.app_password && !/^[a-z]{4}-[a-z]{4}-[a-z]{4}-[a-z]{4}$/.test(settings.app_password)) {
      toast.error('App-Passwort muss im Format xxxx-xxxx-xxxx-xxxx sein (nur Kleinbuchstaben)');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/icloud/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('iCloud Einstellungen gespeichert');
      loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/icloud/sync/all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      // Reload status after a delay
      setTimeout(loadSettings, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync fehlgeschlagen');
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Möchten Sie die iCloud-Verbindung wirklich löschen?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/icloud/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('iCloud-Verbindung gelöscht');
      setSettings({
        apple_id: '',
        app_password: '',
        enabled: true,
        sync_on_create: true,
        sync_on_update: true
      });
      loadSettings();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl">
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/portal-settings')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Zurück zu Portal-Einstellungen
        </Button>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Cloud className="h-6 w-6 text-blue-600" />
          </div>
          iCloud Kontakte Synchronisation
        </h1>
        <p className="text-gray-600 mt-2">
          Synchronisieren Sie Kundenkontakte automatisch mit Ihrem iPhone
        </p>
      </div>

      <div className="space-y-6">
        {/* Status Card */}
        <Card className={status.configured ? 'border-green-200 bg-green-50/30' : 'border-orange-200 bg-orange-50/30'}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {status.configured ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-orange-600" />
              )}
              Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <p className="text-sm text-gray-500">Verbindung</p>
                <p className="font-medium">{status.configured ? 'Konfiguriert' : 'Nicht konfiguriert'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Synchronisation</p>
                <p className="font-medium">{status.enabled ? 'Aktiv' : 'Deaktiviert'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Kontaktgruppe</p>
                <p className="font-medium">{status.contact_group || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Synchronisierte Kontakte</p>
                <p className="font-medium">{status.total_synced}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Letzte Sync</p>
                <p className="font-medium">
                  {status.last_sync ? new Date(status.last_sync).toLocaleString('de-CH') : '-'}
                </p>
              </div>
            </div>
            {status.last_error && (
              <Alert variant="destructive" className="mt-4">
                <AlertDescription>{status.last_error}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AppleIcon size={24} />
              Apple ID Konfiguration
            </CardTitle>
            <CardDescription>
              Verbinden Sie Ihr iCloud-Konto für die automatische Kontaktsynchronisation
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Instructions */}
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                <strong>So erhalten Sie ein App-spezifisches Passwort:</strong>
                <ol className="list-decimal list-inside mt-2 space-y-1 text-sm">
                  <li>Öffnen Sie <a href="https://appleid.apple.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">appleid.apple.com <ExternalLink className="h-3 w-3 inline" /></a></li>
                  <li>Melden Sie sich mit Ihrer Apple ID an</li>
                  <li>Gehen Sie zu <strong>Anmeldung & Sicherheit</strong></li>
                  <li>Klicken Sie auf <strong>App-spezifische Passwörter</strong></li>
                  <li>Klicken Sie auf <strong>Passwort erstellen</strong></li>
                  <li>Geben Sie einen Namen ein (z.B. "Mein Studio")</li>
                  <li>Kopieren Sie das generierte Passwort (Format: <code className="bg-gray-100 px-1 rounded">xxxx-xxxx-xxxx-xxxx</code>)</li>
                </ol>
              </AlertDescription>
            </Alert>

            {/* Enable/Disable */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label className="text-base font-medium">iCloud Sync aktivieren</Label>
                <p className="text-sm text-gray-500">
                  Kontakte automatisch mit iCloud synchronisieren
                </p>
              </div>
              <Switch
                checked={settings.enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
              />
            </div>

            {/* Apple ID */}
            <div>
              <Label htmlFor="apple_id">Apple ID (E-Mail)</Label>
              <Input
                id="apple_id"
                type="email"
                value={settings.apple_id}
                onChange={(e) => setSettings({ ...settings, apple_id: e.target.value })}
                placeholder="ihre-email@icloud.com"
                className="mt-1"
              />
            </div>

            {/* App Password */}
            <div>
              <Label htmlFor="app_password">App-spezifisches Passwort</Label>
              <div className="relative mt-1">
                <Input
                  id="app_password"
                  type={showPassword ? 'text' : 'password'}
                  value={settings.app_password}
                  onChange={(e) => setSettings({ ...settings, app_password: e.target.value.toLowerCase() })}
                  placeholder={status.configured ? '••••-••••-••••-••••' : 'xxxx-xxxx-xxxx-xxxx'}
                  className="pr-10 font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {status.configured ? 'Leer lassen um bestehendes Passwort zu behalten' : 'Format: xxxx-xxxx-xxxx-xxxx (nur Kleinbuchstaben)'}
              </p>
            </div>

            {/* Contact Group */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Label htmlFor="contact_group" className="text-base font-medium flex items-center gap-2">
                <Users className="h-4 w-4 text-blue-600" />
                Kontaktgruppe (Aktive Kunden)
              </Label>
              <p className="text-sm text-gray-600 mb-2">
                Gruppe für <strong>aktive</strong> Kunden in iCloud Kontakten
              </p>
              <Input
                id="contact_group"
                type="text"
                value={settings.contact_group}
                onChange={(e) => setSettings({ ...settings, contact_group: e.target.value })}
                placeholder="StrongFit Kunden"
                className="mt-1 bg-white"
              />
            </div>

            {/* Inactive Group */}
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <Label htmlFor="inactive_group" className="text-base font-medium flex items-center gap-2">
                <Users className="h-4 w-4 text-orange-600" />
                Kontaktgruppe (Inaktive Kunden)
              </Label>
              <p className="text-sm text-gray-600 mb-2">
                Gruppe für <strong>inaktive</strong> Kunden - wird automatisch bei Statusänderung verschoben
              </p>
              <Input
                id="inactive_group"
                type="text"
                value={settings.inactive_group}
                onChange={(e) => setSettings({ ...settings, inactive_group: e.target.value })}
                placeholder="StrongFit Inaktiv"
                className="mt-1 bg-white"
              />
              <p className="text-xs text-gray-500 mt-2">
                💡 <strong>Tipp:</strong> Wenn ein Kunde auf "inaktiv" gesetzt wird, wird er automatisch 
                aus der aktiven Gruppe entfernt und in diese Gruppe verschoben.
              </p>
            </div>

            {/* Sync Options */}
            <div className="space-y-3 pt-4 border-t">
              <Label className="text-base font-medium">Automatische Synchronisation</Label>
              
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm">Bei neuen Kunden</span>
                  <p className="text-xs text-gray-500">Kontakt wird automatisch zu iCloud hinzugefügt</p>
                </div>
                <Switch
                  checked={settings.sync_on_create}
                  onCheckedChange={(checked) => setSettings({ ...settings, sync_on_create: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm">Bei Änderungen</span>
                  <p className="text-xs text-gray-500">Kontakt wird bei Datenänderung aktualisiert</p>
                </div>
                <Switch
                  checked={settings.sync_on_update}
                  onCheckedChange={(checked) => setSettings({ ...settings, sync_on_update: checked })}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* What gets synced */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              Synchronisierte Daten
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { label: 'Vorname', icon: '👤' },
                { label: 'Nachname', icon: '👤' },
                { label: 'E-Mail', icon: '📧' },
                { label: 'Telefon', icon: '📱' },
                { label: 'Adresse', icon: '🏠' },
                { label: 'Geburtsdatum', icon: '🎂' }
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                  <span>{item.icon}</span>
                  <span className="text-sm">{item.label}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-wrap gap-3 justify-between">
          <div className="flex gap-3">
            {status.configured && (
              <>
                <Button 
                  variant="outline" 
                  onClick={handleSyncAll}
                  disabled={syncing || !status.enabled}
                >
                  {syncing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Alle Kunden synchronisieren
                </Button>
                <Button variant="destructive" onClick={handleDelete}>
                  Verbindung löschen
                </Button>
              </>
            )}
          </div>
          
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Wird gespeichert...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Einstellungen speichern
              </>
            )}
          </Button>
        </div>

        {/* Security Note */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
          <div className="flex items-start gap-2">
            <Shield className="h-5 w-5 mt-0.5 flex-shrink-0" />
            <div>
              <strong>Sicherheitshinweis:</strong>
              <p className="mt-1">
                Ihr App-Passwort wird verschlüsselt gespeichert und niemals im Klartext angezeigt.
                Das Passwort wird nur für die sichere Verbindung zu iCloud verwendet.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ICloudSyncSettingsPage;
